import React, {useEffect, useMemo, useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import axios from "axios";
import {Avatar, Box, Button, CardActionArea, Chip, Link, Tooltip, Typography} from "@mui/material";
import {textSimplu} from '../components/DescriereFormatata';
import MuseumIcon from "@mui/icons-material/Museum";
import GroupsIcon from "@mui/icons-material/Groups";
import PaletteIcon from "@mui/icons-material/Palette";
import LayersIcon from "@mui/icons-material/Layers";
import ImageIcon from "@mui/icons-material/Image";
import PersonIcon from "@mui/icons-material/Person";
import {API_BASE, API_ENDPOINT} from "../config";
import {etichetaDataExpozitie} from '../components/dateExpozitie';
import {useAuth} from "../auth/AuthContext";
import CardArta from "../components/CardArta";
import WidgetResursa from "../components/WidgetResursa";
import {gallerySurface} from '../theme/publicTheme';
import {membruPath} from '../utils/urlPublic';
import {hideSplash} from '../splash';

// Widgeturile sunt vizibile pentru toti. Autentificat -> lista din panou (#/admin);
// vizitator -> lista publica, unde exista (lucrari/serii nu au lista publica).
const WIDGETURI = [
    {cheie: 'expozitii', label: 'Expoziții', icon: MuseumIcon, bg: '#dc3545', publicTo: '/expozitii'},
    {cheie: 'artisti', label: 'Artiști', icon: GroupsIcon, bg: '#17a2b8', publicTo: '/artisti'},
    {cheie: 'colectionari', label: 'Colecționari', icon: GroupsIcon, bg: '#8e6b9e', publicTo: '/colectionari'},
    {cheie: 'lucrari', label: 'Lucrări', icon: PaletteIcon, bg: '#28a745'},
    {cheie: 'serii', label: 'Serii', icon: LayersIcon, bg: '#ffc107', fg: '#1f2d3d'},
];

// 29.1: datele expozitiei (cu „Vernisaj: …” pentru cele programate) vin din components/dateExpozitie.

const pozaUrl = (poza) => poza && (/^https?:\/\//i.test(poza)
    ? poza : `${API_BASE.replace(/\/$/, '')}/${poza.replace(/^\/+/, '')}`);

// Avatar-ul MUI cade singur pe iconita cand imaginea lipseste sau nu se incarca.
const CardThumbnail = ({poza, artist = false}) => (
    <Avatar src={pozaUrl(poza) || undefined} alt="" variant={artist ? 'circular' : 'rounded'}
        slotProps={{img: {loading: 'lazy'}}}
        sx={{width: {xs: 72, sm: 96}, height: {xs: 88, sm: 112}, flexShrink: 0, bgcolor: 'action.hover', color: 'primary.main', borderRadius: artist ? '50%' : '4px', '& img': {objectFit: artist ? 'cover' : 'contain'}}}>
        {artist ? <PersonIcon /> : <ImageIcon />}
    </Avatar>
);

const FeaturedImage = ({poza, title}) => {
    const [failed, setFailed] = useState(false);
    return poza && !failed
        ? <Box component="img" src={pozaUrl(poza)} alt={title} onError={() => setFailed(true)}
            sx={{width: '100%', height: {xs: 280, sm: 380, md: 420}, objectFit: 'contain', display: 'block'}} />
        : <Box sx={{height: {xs: 220, md: 340}, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'primary.main'}}><MuseumIcon sx={{fontSize: 64}} /></Box>;
};

const MiniStat = ({icon: Icon, count, label}) => (
    <Chip size="small" variant="outlined" icon={<Icon sx={{fontSize: 14}} />} label={`${count} ${label}`}
        sx={{borderColor: 'divider', color: 'text.secondary', fontSize: 12, height: 22, '& .MuiChip-icon': {color: 'primary.main'}}} />
);

const StatusDot = ({culoare, titlu}) => (
    <Tooltip title={titlu}>
        <Box component="span" role="img" aria-label={titlu}
            sx={{display: 'inline-block', width: 10, height: 10, borderRadius: '50%', bgcolor: culoare, mr: 0.75, verticalAlign: 'middle'}} />
    </Tooltip>
);

const Sectiune = ({titlu, to, loading, gol, children}) => (
    <Box>
        <Typography variant="h3" component="h2" sx={{fontSize: '1.8rem', fontWeight: 400, mb: 2}}>
            <Link component={RouterLink} to={to} color="inherit" underline="hover">{titlu} <Box component="span" sx={{fontFamily: 'sans-serif', fontSize: 18, color: 'primary.main'}}>↗</Box></Link>
        </Typography>
        {loading ? <Typography color="text.secondary">Se încarcă...</Typography>
            : gol ? <Typography color="text.secondary">{gol}</Typography>
                : <Box component="ul" sx={{listStyle: 'none', p: 0, m: 0}}>{children}</Box>}
    </Box>
);

const ElementCard = ({to, poza, artist, children}) => (
    <CardArta component="li" sx={{mb: 1.5}}>
        <CardActionArea component={RouterLink} to={to}
            sx={{display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1.75, p: 1.75, color: 'inherit', '&:hover': {color: 'inherit', textDecoration: 'none'}}}>
            <CardThumbnail poza={poza} artist={artist} />
            <Box sx={{minWidth: 0, overflowWrap: 'anywhere'}}>{children}</Box>
        </CardActionArea>
    </CardArta>
);

const HomePage = () => {
    const {isAuthenticated} = useAuth();
    const [expozitii, setExpozitii] = useState([]);
    const [artisti, setArtisti] = useState([]);
    const [statistici, setStatistici] = useState(null);
    const [loading, setLoading] = useState(true);
    // Ecranul de încărcare (src/splash.js) dispare când pagina are datele - fără „pâlpâire” la deschidere.
    useEffect(() => {
        if (!loading) hideSplash();
    }, [loading]);


    // Punctul galben marcheaza prima expozitie viitoare (cea mai apropiata dataStart >= azi);
    // punctul verde inchis marcheaza expozitiile trecute (dataStart < azi). Expozitiile viitoare
    // ulterioare primei nu primesc punct, la fel cele fara dataStart cunoscuta.
    const {nextExpozitieSlug, expozitiiTrecute} = useMemo(() => {
        const azi = new Date();
        azi.setHours(0, 0, 0, 0);
        const trecute = new Set();
        let urmatoareaSlug = null;
        let urmatoareaData = null;
        expozitii.forEach((e) => {
            if (!e.dataStart) return;
            const data = new Date(e.dataStart);
            if (data < azi) {
                trecute.add(e.slug);
            } else if (!urmatoareaData || data < urmatoareaData) {
                urmatoareaData = data;
                urmatoareaSlug = e.slug;
            }
        });
        return {nextExpozitieSlug: urmatoareaSlug, expozitiiTrecute: trecute};
    }, [expozitii]);

    useEffect(() => {
        Promise.all([
            axios.get(`${API_ENDPOINT}/public/expozitii`).then((r) => r.data.items).catch(() => []),
            axios.get(`${API_ENDPOINT}/public/artisti/random`).then((r) => r.data.items).catch(() => []),
        ]).then(([e, a]) => {
            setExpozitii(e);
            setArtisti(a);
            setLoading(false);
        });
        axios.get(`${API_ENDPOINT}/public/statistici`).then((r) => setStatistici(r.data)).catch(() => setStatistici({}));
    }, []);

    const featured = expozitii.find((e) => e.slug === nextExpozitieSlug) || expozitii.find((e) => e.poza) || expozitii[0];

    return (
        <Box>
            <Box sx={{maxWidth: 1200, mx: 'auto', px: {xs: 2, sm: 4}, pt: {xs: 4, md: 7}, pb: 6}}>
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: featured ? '1.15fr 1fr' : '1fr'}, gap: {xs: 3, md: 7}, alignItems: 'center', mb: {xs: 4, md: 7}}}>
                    <Box sx={{order: {xs: 0, md: featured ? 1 : 0}}}>
                        <Typography sx={{color: 'primary.main', letterSpacing: '.18em', fontSize: 12, textTransform: 'uppercase', mb: 2}}>Artă · Oameni · Întâlniri</Typography>
                        <Typography variant="h1" sx={{fontSize: {xs: '2.8rem', sm: '3.8rem', md: '4.4rem'}, lineHeight: 1.08, letterSpacing: '-.035em', maxWidth: 620}}>
                            Un loc pentru artă și colecții rare.<br />Pentru artiști, colecționari și toți cei care iubesc lucrurile frumoase.
                        </Typography>
                        <Typography sx={{color: 'text.secondary', fontSize: 18, lineHeight: 1.7, maxWidth: 440, mt: 3, mb: 3}}>
                            Descoperă expoziții, cunoaște artiști și explorează poveștile din spatele lucrărilor.
                        </Typography>
                        <Button component={RouterLink} to="/expozitii" variant="contained" sx={{px: 3, py: 1.25}}>Explorează expozițiile ↗</Button>
                        <Button component={RouterLink} to="/artisti" sx={{ml: 1, px: 2, py: 1.25}}>Cunoaște artiștii</Button>
                    </Box>
                    {featured && <Box component="article" sx={{minWidth: 0, order: {xs: 1, md: 0}}}>
                        <Box component={RouterLink} to={`/expozitii/${featured.slug}`} sx={{display: 'block', color: 'inherit', '&:hover': {color: 'inherit', textDecoration: 'none'}}}>
                            <Box sx={{p: {xs: 2, sm: 3}, bgcolor: gallerySurface, border: 1, borderColor: 'divider'}}>
                                <FeaturedImage key={featured.poza} poza={featured.poza} title={featured.numeExpozitie} />
                            </Box>
                            <Box sx={{pt: 2, display: 'flex', justifyContent: 'space-between', gap: 2}}>
                                <Box sx={{minWidth: 0, overflowWrap: 'anywhere'}}>
                                    <Typography sx={{fontSize: 11, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '.14em', mb: .5}}>{featured.slug === nextExpozitieSlug ? 'Următoarea expoziție' : 'Din expozițiile noastre'}</Typography>
                                    <Typography variant="h2" sx={{fontSize: 26}}>{featured.numeExpozitie}</Typography>
                                    <Typography sx={{fontSize: 14, color: 'text.secondary', mt: .5}}>{etichetaDataExpozitie(featured)}{featured.online === true ? ' · Online' : featured.address ? ` · ${featured.address}` : ''}</Typography>
                                </Box>
                                <Box aria-hidden="true" sx={{color: 'primary.main', fontSize: 28}}>↗</Box>
                            </Box>
                        </Box>
                    </Box>}
                </Box>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4}}>
                    {WIDGETURI.map(({cheie, publicTo, ...widget}) => (
                        <WidgetResursa key={cheie} {...widget} subtle
                            total={statistici ? (statistici[cheie] ?? '–') : undefined}
                            to={isAuthenticated ? (['artisti', 'colectionari'].includes(cheie)
                                ? `/admin/artisti?filter=${encodeURIComponent(JSON.stringify({type: cheie === 'colectionari' ? 'colectionar' : 'artist'}))}`
                                : `/admin/${cheie}`) : publicTo} />
                    ))}
                </Box>

                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 3}}>
                    <Sectiune titlu="Expoziții" to="/expozitii" loading={loading} gol={!expozitii.length && 'Nicio expoziție încă.'}>
                        {expozitii.map((e) => (
                            <ElementCard key={e.slug} to={`/expozitii/${e.slug}`} poza={e.poza}>
                                {e.slug === nextExpozitieSlug && <StatusDot culoare="#f6c000" titlu="Următoarea expoziție" />}
                                {expozitiiTrecute.has(e.slug) && <StatusDot culoare="#1e5631" titlu="Expoziție trecută" />}
                                <strong>{e.numeExpozitie}</strong>
                                <Typography variant="body2" color="text.secondary" sx={{fontSize: 13}}>
                                    {etichetaDataExpozitie(e)}
                                    {e.online === true ? ' · Online' : e.address ? ` · ${e.address}` : ''}
                                </Typography>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75}}>
                                    <MiniStat icon={PersonIcon} count={e.numarArtisti ?? 0} label="artiști" />
                                    <MiniStat icon={ImageIcon} count={e.numarLucrari ?? 0} label="lucrări" />
                                </Box>
                            </ElementCard>
                        ))}
                    </Sectiune>

                    <Sectiune titlu="Artiști" to="/artisti" loading={loading} gol={!artisti.length && 'Niciun artist încă.'}>
                        {artisti.map((a) => (
                            <ElementCard key={a.slug} to={membruPath(a.type, a.slug)} poza={a.poza} artist>
                                <strong>{a.nume}</strong>
                                {a.shortCv && <Typography variant="body2" color="text.secondary" sx={{fontSize: 13}}>{textSimplu(a.shortCv)}</Typography>}
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75}}>
                                    <MiniStat icon={ImageIcon} count={a.numarLucrari ?? 0} label="lucrări" />
                                </Box>
                            </ElementCard>
                        ))}
                    </Sectiune>
                </Box>
            </Box>
        </Box>
    );
};

export default HomePage;
